/**
 * Pure geometry for the opening-sketch "distance to element" op. No THREE, no
 * UI — just 2D math in the XY (floor) plane, so it's unit-testable.
 *
 * The opening is a span [a, b] on its wall centerline. It can only slide ALONG
 * that wall (a 1-D offset parameter). A distance target is either another wall
 * centerline SEGMENT [p, q] or a NODE point n. Distance is always the SHORTEST
 * distance between the opening span and the target (clamped to the segments —
 * never the infinite line), matching "closest point of the opening to it".
 */

import { pointAtArcLength, closestPointOnEdge, lineArcIntersections, edgeIsCurved } from '../../../../utils/skeleton/arcGeometry';

export interface Pt {
  x: number;
  y: number;
}

const EPS = 1e-9;

function sub(a: Pt, b: Pt): Pt {
  return { x: a.x - b.x, y: a.y - b.y };
}
function add(a: Pt, b: Pt): Pt {
  return { x: a.x + b.x, y: a.y + b.y };
}
function scale(a: Pt, s: number): Pt {
  return { x: a.x * s, y: a.y * s };
}
function dot(a: Pt, b: Pt): number {
  return a.x * b.x + a.y * b.y;
}


/**
 * A distance target: a wall centerline SEGMENT (p0..p1), or a NODE point where
 * p0 === p1.
 */
export interface DistanceTarget {
  p0: Pt;
  p1: Pt;
}

/**
 * The opening's footprint on its wall, parameterized by along-wall offset.
 * `edgeStart`/`edgeEnd` are the wall edge endpoints; the opening spans
 * [offset, offset + length] along that edge (both in meters from edgeStart).
 */
export interface OpeningOnEdge {
  edgeStart: Pt;
  edgeEnd: Pt;
  length: number;
  /** Arc curvature of the wall edge (0/absent = straight). Along-wall
   * coordinates are ARC lengths when set. */
  bulge?: number;
}

/** World point on the edge at along-wall `offset` (meters from edgeStart —
 *  arc length on curved edges). */
function edgePointAtOffset(o: OpeningOnEdge, offset: number): Pt {
  if (edgeIsCurved(o)) return pointAtArcLength(o.edgeStart, o.edgeEnd, o.bulge, offset).point;
  const d = sub(o.edgeEnd, o.edgeStart);
  const len = Math.hypot(d.x, d.y);
  if (len < EPS) return { ...o.edgeStart };
  const t = offset / len;
  return add(o.edgeStart, scale(d, t));
}

/** World point on the opening's wall axis at along-wall coordinate `s` (meters
 *  from edgeStart). `s` may be outside [0, edgeLength] (the axis is extended). */
export function pointAtAlongCoord(o: OpeningOnEdge, s: number): Pt {
  return edgePointAtOffset(o, s);
}

/** Unit direction of the opening's wall, from edgeStart toward edgeEnd. */
function edgeUnit(o: OpeningOnEdge): Pt | null {
  const d = sub(o.edgeEnd, o.edgeStart);
  const len = Math.hypot(d.x, d.y);
  if (len < EPS) return null;
  return { x: d.x / len, y: d.y / len };
}

/** Directions within ~1° of (anti-)parallel are treated as colinear (no usable
 *  crossing) — the angle test, not a raw cross product, so long target walls
 *  don't read as "crossing" just because their length vector is big. */
const PARALLEL_SIN_EPS = 0.0175; // sin(1°)

/**
 * Along-wall coordinate (meters from edgeStart, measured along the opening's own
 * wall axis) that the distance is measured TO. Always defined (never null) so
 * sliding can always change the distance — the measurement is ALWAYS colinear
 * with the opening:
 *   - NODE: the node projected onto the axis line.
 *   - CROSSING WALL (perpendicular / angled): the intersection of the target's
 *     infinite line with the opening's axis line.
 *   - PARALLEL / COLINEAR WALL: the target's NEAREST ENDPOINT to the opening SPAN
 *     (whichever end is closer to `refCoord`), projected onto the axis — i.e. the
 *     adjacent end of that wall, not its far end. Sliding then moves the opening
 *     toward/past that end.
 *
 * `refCoord` is the along-axis coordinate the "nearest end" is judged against
 * (the opening's near edge toward the target) — only used in the parallel case.
 */
export function targetAlongCoord(o: OpeningOnEdge, target: DistanceTarget, refCoord: number): number {
  if (edgeIsCurved(o)) return targetAlongCoordOnArc(o, target, refCoord);
  const u = edgeUnit(o);
  if (!u) return refCoord;
  const projCoord = (p: Pt): number => dot(sub(p, o.edgeStart), u);
  const isNode = Math.abs(target.p0.x - target.p1.x) < EPS && Math.abs(target.p0.y - target.p1.y) < EPS;
  if (isNode) return projCoord(target.p0);

  // Parallelism via the ANGLE between the two directions (normalized), so a long
  // colinear wall isn't mistaken for a crossing one. sin(angle) = |cross(û, v̂)|.
  const v = sub(target.p1, target.p0);
  const vLen = Math.hypot(v.x, v.y);
  const sin = vLen > EPS ? Math.abs(u.x * (v.y / vLen) - u.y * (v.x / vLen)) : 0;
  if (sin >= PARALLEL_SIN_EPS) {
    // Crossing: intersect target's infinite line with the opening axis.
    //   edgeStart + s·u == p0 + r·v  →  s = det([w −v]) / det([u −v]).
    const w = sub(target.p0, o.edgeStart);
    const denom = u.x * -v.y - u.y * -v.x;
    return (w.x * -v.y - w.y * -v.x) / denom;
  }
  // Parallel / colinear: measure to the target's NEAREST endpoint to the opening.
  const c0 = projCoord(target.p0);
  const c1 = projCoord(target.p1);
  return Math.abs(c0 - refCoord) <= Math.abs(c1 - refCoord) ? c0 : c1;
}

/**
 * Curved-wall variant of {@link targetAlongCoord}: along-wall coordinates are
 * arc lengths on the opening's arc. A crossing target contributes its
 * line × arc intersection nearest `refCoord`; nodes and non-crossing targets
 * project to their closest point on the arc. (Unlike the straight case the
 * axis isn't extended past the edge — the opening can't slide off it anyway.)
 */
function targetAlongCoordOnArc(o: OpeningOnEdge, target: DistanceTarget, refCoord: number): number {
  const arcCoord = (p: Pt): number => closestPointOnEdge(p, o.edgeStart, o.edgeEnd, o.bulge).s;
  const isNode = Math.abs(target.p0.x - target.p1.x) < EPS && Math.abs(target.p0.y - target.p1.y) < EPS;
  if (isNode) return arcCoord(target.p0);

  const v = sub(target.p1, target.p0);
  const vLen = Math.hypot(v.x, v.y);
  if (vLen > EPS) {
    const hits = lineArcIntersections(target.p0, { x: v.x / vLen, y: v.y / vLen }, o.edgeStart, o.edgeEnd, o.bulge);
    let best: number | null = null;
    for (const h of hits) {
      const s = arcCoord(h);
      if (best === null || Math.abs(s - refCoord) < Math.abs(best - refCoord)) best = s;
    }
    if (best !== null) return best;
  }
  // No crossing — nearest target endpoint (judged in arc coords).
  const c0 = arcCoord(target.p0);
  const c1 = arcCoord(target.p1);
  return Math.abs(c0 - refCoord) <= Math.abs(c1 - refCoord) ? c0 : c1;
}

/**
 * Distance from the opening CENTER to the target, measured along the opening's
 * wall axis (colinear with the opening). Always defined.
 */
export function distanceAtOffset(o: OpeningOnEdge, offset: number, target: DistanceTarget): number {
  const centerCoord = offset + o.length / 2;
  const targetCoord = targetAlongCoord(o, target, centerCoord);
  return Math.abs(centerCoord - targetCoord);
}

/**
 * Solve for the along-wall offset at which the opening's along-axis distance to
 * `target` equals `desired`, clamped to [0, maxOffset]. Distance is
 * |centerCoord − targetCoord| with centerCoord = offset + length/2, so this is a
 * closed-form solve: offset = targetCoord − length/2 ± desired. We pick the sign
 * whose result is nearest `currentOffset` (minimal, stable movement).
 */
export function solveOffsetForDistance(o: OpeningOnEdge, target: DistanceTarget, desired: number, currentOffset: number, maxOffset: number): number {
  if (maxOffset <= EPS) return 0;
  // Judge the parallel "nearest end" against the current center coord.
  const targetCoord = targetAlongCoord(o, target, currentOffset + o.length / 2);
  const base = targetCoord - o.length / 2;
  const roots = [base - desired, base + desired].map((r) => Math.max(0, Math.min(maxOffset, r)));

  // Nearest clamped root to the current offset — minimal, stable movement.
  let best = roots[0];
  let bestDist = Math.abs(best - currentOffset);
  for (const r of roots) {
    const d = Math.abs(r - currentOffset);
    if (d < bestDist) {
      bestDist = d;
      best = r;
    }
  }
  return Math.max(0, Math.min(maxOffset, best));
}
